// imports
import { Address } from 'abitype';
import {
  ethers, Networkish, InterfaceAbi, TransactionReceipt, AbstractProvider
} from 'ethers';
import { L2Chain } from './wagmi/chains';
import { IQuestClaimBody } from './types';
import { IContractProps } from './wagmi/types';
import WLForwarder from './contract/wlForwarder';
import { autoTask as TASK_API, quests as QUEST_API } from '../api';

// interfaces / types / consts
type QuestAction = 'START_QUEST' | 'END_QUEST';
interface IQuestActionBody extends IQuestClaimBody {
  action: QuestAction;
  eoaAddress: Address;
  isCampaign?: boolean;
}
const DEADLINE = Number(process.env.REACT_APP_RELAYER_DEADLINE || (60 * 5));
const DOMAIN = {
  version: '1',
  chainId: L2Chain.id,
  name: 'ERC2771Forwarder',
  verifyingContract: WLForwarder.CONTRACT_ADDRESS
};
const FORWARD_REQUEST = [
  { name: 'from', type: 'address' },
  { name: 'to', type: 'address' },
  { name: 'value', type: 'uint256' },
  { name: 'gas', type: 'uint256' },
  { name: 'nonce', type: 'uint256' },
  { name: 'deadline', type: 'uint48' },
  { name: 'data', type: 'bytes' }
];

// private methods
function stringToInt(inputStr: string): number {
  const walletHash = ethers.keccak256(inputStr);
  return Number(BigInt(walletHash) % BigInt(Number.MAX_SAFE_INTEGER));
}
function getTransactionReceipt(provider: AbstractProvider, txHash: string): Promise<TransactionReceipt> {
  return provider.getTransactionReceipt(txHash).then((receipt) => {
    if (receipt) { return Promise.resolve(receipt); }
    return new Promise((resolve) => { setTimeout(() => resolve(getTransactionReceipt(provider, txHash)), 500); });
  });
}

// exports
export function sendQuestActionTransaction(props: IQuestActionBody): Promise<TransactionReceipt | null> {
  const {
    questId, isCampaign, pinId, heroTokens,
    action, delegateWallet, eoaAddress, captchaResponse
  } = props;
  const signerKey = delegateWallet.privateKey;
  const provider = ethers.getDefaultProvider(L2Chain.rpcUrls.public.http[0] as Networkish);
  const signer = new ethers.Wallet(signerKey, provider);

  const delegateAddress = delegateWallet.address;
  const signMessage = ethers.solidityPacked(['string', 'string', 'uint[]'], [questId, pinId, heroTokens]);
  const signature = signer.signMessageSync(signMessage);
  return QUEST_API.action({
    pinId,
    action,
    questId,
    signature,
    heroTokens,
    eoaAddress,
    delegateAddress,
    isCampaign: isCampaign || false,
    captchaResponse: captchaResponse || ''
  }, stringToInt(delegateAddress)).then((txHash) => getTransactionReceipt(provider, txHash));
}
export function sendMetaTransaction(props: IContractProps, signerKey: Address, gas = 600000): Promise<TransactionReceipt | null> {
  const provider = ethers.getDefaultProvider(L2Chain.rpcUrls.public.http[0] as Networkish);
  const signer = new ethers.Wallet(signerKey, provider);
  const to = props.address!;
  const from = signer.address! as Address;
  const REQUEST = {
    to,
    gas,
    from,
    data: '',
    value: '0',
    nonce: '',
    deadline: 0,
    signature: ''
  };
  return WLForwarder.nonces(from)
    .then((nonce) => {
      REQUEST.nonce = nonce;
      const contract = new ethers.Contract(to, props.abi! as InterfaceAbi, signer);
      return contract[props.functionName!].populateTransaction(...props.args!);
    })
    .then((transaction) => {
      REQUEST.data = transaction.data;
      REQUEST.deadline = Math.round(Date.now() / 1000) + DEADLINE;
      return signer.signTypedData(DOMAIN, { ForwardRequest: FORWARD_REQUEST }, REQUEST);
    })
    .then((signature) => {
      REQUEST.signature = signature;
      return TASK_API.post(REQUEST, stringToInt(from));
    })
    .then((txHash) => getTransactionReceipt(provider, txHash));
}
export default { sendMetaTransaction, sendQuestActionTransaction };
